/**
 * Clear browser-persisted tenant-scoped UI state on logout / session end.
 * Prevents cross-account leakage of connections, persona, inbox composer, and related caches.
 */
import { useConnectionsStore } from "@/store/useConnectionsStore";
import { usePersonaStore } from "@/store/usePersonaStore";
import { useTeamStore } from "@/store/useTeamStore";
import { useBillingStore } from "@/store/useBillingStore";
import { useMentionsStore } from "@/store/useMentionsStore";
import { useSafetyStore } from "@/store/useSafetyStore";
import { useOnboardingStore } from "@/store/useOnboardingStore";
import { useInstanceStateStore } from "@/store/useInstanceStateStore";
import { useSignupStore } from "@/store/useSignupStore";

const TENANT_SCOPED_STORAGE_KEYS = [
  "sellix.connections.v1",
  "sellix.persona.v1",
  "sellix.team.v1",
  "sellix.billing.v1",
  "sellix.mentions.v1",
  "sellix.safety.v1",
  "sellix.onboarding.v1",
  "sellix.instanceState.v1",
  "sellix.signup.v1",
  "sellix.contentQueue.v1",
];

const TENANT_SCOPED_STORAGE_PREFIXES = [
  "sellyx.inbox.composerExpanded.",
];

function clearPersist(store) {
  try {
    store?.persist?.clearStorage?.();
  } catch {
    // ignore
  }
}

function clearPrefixedStorage(storage, prefixes) {
  if (!storage) return;
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && prefixes.some((prefix) => key.startsWith(prefix))) {
        keys.push(key);
      }
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // ignore
  }
}

export function clearTenantScopedClientState() {
  if (typeof window === "undefined") return;

  for (const key of TENANT_SCOPED_STORAGE_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  }

  clearPrefixedStorage(window.localStorage, TENANT_SCOPED_STORAGE_PREFIXES);
  clearPrefixedStorage(window.sessionStorage, TENANT_SCOPED_STORAGE_PREFIXES);

  clearPersist(useConnectionsStore);
  clearPersist(usePersonaStore);
  clearPersist(useTeamStore);
  clearPersist(useBillingStore);
  clearPersist(useMentionsStore);
  clearPersist(useSafetyStore);
  clearPersist(useOnboardingStore);
  clearPersist(useInstanceStateStore);
  clearPersist(useSignupStore);
}
